import {
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Select,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  HStack,
  FormErrorMessage,
  Box,
  Container,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { FiEye, FiEyeOff, FiArrowLeft } from "react-icons/fi";
import { useState, useEffect } from "react";
import InputMask from "react-input-mask";
import AsyncSelect from "react-select/async";
import { FaUserPlus /* , FaUsers */ } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams  } from "react-router-dom";
import { RegisterUserFormValidationSchema } from "./registerUserFormValidation";
import { Field, Form, Formik } from "formik";
import { User } from "@/models/user.model";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import {
  getFilteredUsers,
  getUserById,
  registerUser,
  updateUser,
} from "@/services/user.service";
import { documentFormatter } from "@/utils/documentFormatter";
import { formatPhone } from "@/utils/formatPhone";
import { getRoles } from "@/services/role.service";
import { Role } from "@/models/role.model";
import { removeMask } from "@/utils/removeMask";
import { CreditPromoter } from "@/models/creditPromoter.model";
import { getCreditPromoterById } from "@/services/creditPromoter.service";
import { createSelfService } from "@/services/selfService.service";
import { createSlug } from "@/utils/createSlug";
import FinancialSettingsForm from "../FinancialSettingsForm";
import { associateFinancialSettings } from "@/services/financialSettings.service";
import { FinancialSettingsTax } from "@/models/financialSettings.model";
import { CorbanCreditModalityEnum } from "@/models/corban.model";
import { useBanks } from "@/hooks/useBanks";


export default function RegisterUserForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [actualUser, setActualUser] = useState<User>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [searchSupervisor, setSearchSupervisor] = useState<string>("");
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [searchParams] = useSearchParams();
  const paramCorbanId = searchParams.get("corbanId");
  const paramCreditPromoterId = searchParams.get("creditPromoterId");

  // Financial Settings states
  const [isAllowedFinancialSettings, setIsAllowedFinancialSettings] = useState<boolean>(false);
  const [formValuesForFinancialSettings, setFormValuesForFinancialSettings] = useState<{
    document: string;
    corbanId: number | undefined;
    creditPromoterId: number | undefined;
  } | null>(null);
  const [pendingFinancialSettingsId, setPendingFinancialSettingsId] = useState<string | null>(null);
  const [financialSettingsButtonText, setFinancialSettingsButtonText] = useState<string>("Criar Dados Financeiros");
  const [financialSettingsModalTitle, setFinancialSettingsModalTitle] = useState<string>("Criar Dados Financeiros");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { user } = useEnsureAuthenticated();
  const toast = useToast();
  const { id } = useParams();
  const isNew = !id;
  const { data: banks = [],isLoading: loadingBanks } = useBanks({ enabled: isOpen });
  const isPromoter = !!(user?.creditPromoterId && !user.corbanId);
  const [creditPromoter, setCreditPromoter] = useState<CreditPromoter | null>(
    null
  );
  const navigate = useNavigate();
  // console.log("paramCorbanId:", paramCorbanId);
  // console.log("paramCreditPromoterId:", paramCreditPromoterId);
  // console.log("user:", user); 

  const setActualUserData = async () => {
    if(user?.roles?.[0].name === "MASTER_CORBAN_COM_PROMOTORA_SEM_CADASTRO") {
      // navigateBack();
      navigate(-1);      
      return null;
    }
    if (!isNew) {
      const userToEdit = await getUserById(id);
      if (userToEdit) {
        // console.log("Usuário encontrado:", userToEdit);
        setActualUser(userToEdit);

        if (userToEdit.roles && userToEdit.roles.length > 0) {
          setSelectedRole(
            userToEdit.roles[0].name
              .toLowerCase()
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")
          );
        }
        return userToEdit;
      } else {
        console.log("Usuário não encontrado");
        // TODO: tratar caso de usuário não encontrado
      }
    }
    return null;
  }

  const fetchRoles = async (userLoaded?: User) => {
    try {
      const rolesResponse = await getRoles();
      let formattedRoles = [...rolesResponse];
      const creditPromoterId = paramCreditPromoterId ?? userLoaded?.creditPromoterId;
      const corbanId = paramCorbanId ?? userLoaded?.corbanId;
      // console.log("creditPromoterId:", creditPromoterId);
      // console.log("corbanId:", corbanId);
      // console.log("roles:", formattedRoles);
      if (corbanId && corbanId.toString() === '1') {
        formattedRoles = formattedRoles.filter(
          (role) =>
            ["GRANATECH", "GERENTE_COMERCIAL"].includes(
              role.name
            )
        );
      } else if (corbanId && 
        (userLoaded?.roles?.[0]?.name === "MASTER_CORBAN_COM_PROMOTORA" ||
        userLoaded?.roles?.[0]?.name === "MASTER_CORBAN_SEM_PROMOTORA" ||
        userLoaded?.roles?.[0]?.name === "MASTER_CORBAN_COM_PROMOTORA_SEM_CADASTRO"
      )) {
        formattedRoles = formattedRoles.filter(
          (role) =>
            [userLoaded?.roles?.[0]?.name].includes(
                role.name
              )
        );
      } else if (corbanId) {

        formattedRoles = formattedRoles.filter(
          (role) =>
            ["VENDEDOR",
              "SUPERVISOR",
              "CONSULTA",
              "CREATE_USER"].includes(
                role.name
              )
        );
      } else if (creditPromoterId) {
        formattedRoles = formattedRoles.filter((role) =>
          [
            "MASTER_PROMOTORA",
            "USUARIO_INTERNO_PROMOTORA",
            "SUPORTE",
            "FINANCEIRO",
          ].includes(role.name)
        );
      }

      formattedRoles = formattedRoles.map((role) => ({
        ...role,
        name: formatRoleName(role.name),
      }));
      // console.log("roles filtrados:", formattedRoles);
      setRoles(formattedRoles);
      // await fetchSupervisors(formattedRoles);
    } catch (error: any) {
      toast({
        title: "Error ao obter a lista de cargos",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchCreditPromoter = async (userLoaded?: User) => {
    if (!paramCreditPromoterId || !userLoaded?.creditPromoterId) {
      return
    }
    // if (!user?.creditPromoterId) {
    //   return
    // }
    try {
      const creditPromoterResponse = await getCreditPromoterById(
        paramCreditPromoterId || userLoaded?.creditPromoterId?.toString()
      );

      setCreditPromoter(creditPromoterResponse);
    } catch (error: any) {
      toast({
        title: `Error ao obter a promotora de id: ${user?.creditPromoterId}`,
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchSupervisors = async (formattedRoles?: Role[]) => {
    setLoadingSupervisors(true);
    try {
      const creditPromoterId = paramCreditPromoterId ?? actualUser?.creditPromoterId;
      const corbanId = paramCorbanId ?? actualUser?.corbanId;

      const filters = {
        page: 1,
        pageSize: 10,
        roleId:
          roles?.length > 0
            ? roles.find((role) => role.name === "Supervisor")?.id
            : formattedRoles?.find((role) => role.name === "Supervisor")?.id,
        corbanId: Number(corbanId!),
        creditPromoterId: Number(creditPromoterId!),
        enabled: true,
      };

      const response = await getFilteredUsers(filters);

      setSupervisors(response.data);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar supervisores",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingSupervisors(false);
    }
  };
  const formatRoleName = (roleName: string) => {
    if (roleName === "MASTER_CORBAN_COM_PROMOTORA_SEM_CADASTRO")
      return "Master Corban (Não pode criar Usuários)";
    return roleName
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleRoleChange = async (
    event: React.ChangeEvent<HTMLSelectElement>,
    setFieldValue: (field: string, value: any) => void,
    currentCorbanId?: string
  ) => {
    const selectedRoleId = event.target.value;

    const selectedRole = roles.find((role) => role.id === selectedRoleId);

    if (selectedRole) {
      setSelectedRole(selectedRole.name);
      setFieldValue("roles", [selectedRole]);
    } else {
      setSelectedRole("");
      setFieldValue("roles", []);
    }

    if (selectedRole?.name === "Vendedor") {
      await fetchSupervisors();
    } else {
      setSupervisors([]);
    }

    // Verificar permissão de financial settings para novo usuário
    if (isNew && selectedRoleId) {
      try {
        const corbanIdToCheck = currentCorbanId || paramCorbanId || actualUser?.corbanId;
        if (corbanIdToCheck) {
          const response = await getFilteredUsers({
            page: 1,
            pageSize: 1,
            roleId: selectedRoleId,
            corbanId: Number(corbanIdToCheck),
          });
          setIsAllowedFinancialSettings(
            response.isAllowedFinancialSettingsCorbanId === true
          );
        } else {
          setIsAllowedFinancialSettings(false);
        }
      } catch (error: any) {
        console.error("Erro ao verificar permissão de financial settings:", error);
        setIsAllowedFinancialSettings(false);
      }
    }
  };

  useEffect(() => {
    // console.log("useEffect:", actualUser);
    const fetchData = async () => {
      // console.log("fetchData:", actualUser);
      try {
        const userToEdit = await setActualUserData();
        await fetchRoles(userToEdit!);
        await fetchCreditPromoter(userToEdit!);
      } catch (err: any) {
        toast({
          title: "Erro ao obter a lista de Corbans",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
    if (!actualUser) {
      fetchData()
    }
  }, []);

  // Financial Settings useEffects
  useEffect(() => {
    const hasFinancialSettings =
      !isNew &&
      actualUser?.financialSettings &&
      actualUser.financialSettings.length > 0 &&
      actualUser.financialSettings[0]?.FinancialSettings;

    if (hasFinancialSettings) {
      setFinancialSettingsButtonText("Editar Cadastros Financeiros");
      setFinancialSettingsModalTitle(
        `Editar Cadastros Financeiros - ${actualUser?.name} ${actualUser?.lastName}`
      );
    } else {
      setFinancialSettingsButtonText("Criar Dados Financeiros");
      setFinancialSettingsModalTitle("Criar Dados Financeiros");
    }
  }, [isNew, actualUser]);

  // function getValidCorbanId(
  //   isPromoter: boolean,
  //   user?: Partial<User>,
  //   actualUserCorbanId?: Number | null,
  //   paramCorbanId?: Number | null
  // ) {
  //   if (actualUserCorbanId !== undefined && actualUserCorbanId !== null) {
  //     return actualUserCorbanId;
  //   }

  //   if (paramCorbanId && !isNaN(Number(paramCorbanId))) {
  //     return Number(paramCorbanId);
  //   }

  //   if (
  //     !isPromoter &&
  //     user?.corbanId !== undefined &&
  //     user?.corbanId !== null
  //   ) {
  //     return user.corbanId;
  //   }

  //   return undefined;
  // }

  const handleOpenFinancialSettingsModal = (e: React.MouseEvent<HTMLButtonElement>, formValues: any) => {
    e.preventDefault();
    if (
      (!isNew && actualUser?.isAllowedFinancialSettingsCorbanId) ||
      (isNew && isAllowedFinancialSettings)
    ) {
      // No modo de criação, capturar os valores do formulário
      if (isNew) {
        setFormValuesForFinancialSettings({
          document: removeMask(formValues.document || ''),
          corbanId: formValues.corbanId ? Number(formValues.corbanId) : undefined,
          creditPromoterId: formValues.creditPromoterId ? Number(formValues.creditPromoterId) : undefined,
        });
      }
      onOpen();
    }
  };

  return (
    <Container maxW="4xl" minH="fit-content">
      <Box
        boxShadow="0px 5px 8px rgba(0, 0, 0, 0.15), 0px -3px 6px rgba(0, 0, 0, 0.1)"
        p={10}
        borderRadius="xl"
      >
        <HStack
          justify="space-between"
          mb={12}
          flexDirection={{ base: "column", md: "row" }}
          spacing={{ base: 6, md: 6 }}
        >
          <Heading size="xl" fontFamily="Lexend Deca">
            {" "}
            {!isNew ? "Editar Usuário" : "Adicionar Usuário"}
          </Heading>
          {!isNew && (
            <Heading as="b" size="md">
              Id do Usuário - {actualUser?.id}
            </Heading>
          )}
          <HStack spacing={6} w={{ base: "full", md: "auto" }}>
            {/* Botão "Adicionar em Lote" comentado para possível uso futuro */}
            {/* <Button
              colorScheme="secondary"
              color="white"
              px={{ base: 8, md: 8 }}
              py={{ base: 8, md: 4 }}
              borderRadius="md"
              fontWeight="bold"
              display="flex"
              alignItems="center"
              w={{ base: "100%", md: "auto" }}
              leftIcon={<FaUsers size={24} />}
              onClick={() =>
                navigate(
                  routes.find((route) => route.name === "user-insert-batch")
                    ?.path || "/"
                )
              }
              fontFamily="Lexend Deca"
              fontSize={{ base: "lg", md: "lg" }}
              h={{ base: "70px", md: "50px" }}
              lineHeight={{ base: "28px", md: "normal" }}
              whiteSpace="normal"
            >
              Adicionar em Lote
            </Button> */}
            <Button
              bg="gray.500"
              color="white"
              px={{ base: 8, md: 8 }}
              py={{ base: 8, md: 4 }}
              borderRadius="md"
              fontWeight="bold"
              _hover={{ bg: "gray.400" }}
              display="flex"
              alignItems="center"
              w={{ base: "100%", md: "auto" }}
              leftIcon={<FiArrowLeft size={24} />}
              onClick={() => navigate(-1)}
              fontFamily="Lexend Deca"
              fontSize={{ base: "lg", md: "lg" }}
              h={{ base: "70px", md: "50px" }}
              lineHeight={{ base: "28px", md: "normal" }}
              whiteSpace="normal"
            >
              Voltar
            </Button>
          </HStack>
        </HStack>

        <Formik
          initialValues={{
            id: actualUser?.id ?? undefined,
            name: actualUser?.name ?? "",
            lastName: actualUser?.lastName ?? "",
            email: actualUser?.email ?? "",
            password: "",
            confirmPassword: "",
            cellphone: actualUser?.cellphone
              ? formatPhone(actualUser.cellphone)
              : "",
            document: actualUser?.document
              ? documentFormatter(actualUser.document)
              : "",
            corbanId: actualUser?.corbanId ? actualUser?.corbanId : Number(paramCorbanId) > 0 ? Number(paramCorbanId) : undefined,
            roles: actualUser?.roles ?? [],
            creditPromoterId:
              actualUser?.creditPromoterId ||  paramCreditPromoterId || user?.creditPromoterId,
            supervisorId: actualUser?.ownerId ?? null,
          }}
          enableReinitialize
          onSubmit={async (values) => {
            const cleanDocument = removeMask(values.document);
            const cleanCellphone = removeMask(values.cellphone);

            // console.log("userData:", values);
            const userData: Partial<User> = {
              id: values.id,
              name: values.name,
              email: values.email,
              lastName: values.lastName,
              document: cleanDocument,
              cellphone: cleanCellphone,
              corbanId: values.corbanId ?? undefined,
              roles: values.roles,
              creditPromoterId: values.creditPromoterId ? Number(values.creditPromoterId) : undefined,
              ownerId: values.supervisorId,
            };

            if (values.password) {
              userData.password = values.password;
            }

            try {
              let userRegistered;
              if (isNew) {
                userRegistered = await registerUser(userData);
                if (userRegistered.corbanId) {
                  try {
                    await createSelfService({
                      slug: createSlug(`${userRegistered.name} ${userRegistered.lastName}`),
                      corbanId: userRegistered.corbanId!,
                      name: `${userRegistered.name} ${userRegistered.lastName}`,
                      userId: userRegistered.id,
                      isUserCampaign: true,
                    });
                  } catch (selfServiceError) {
                    console.error(
                      "Error creating self service:",
                      selfServiceError
                    );
                  }
                }
              } else {
                userRegistered = await updateUser(userData);
              }

              // Associar financial settings após criar o usuário
              if (isNew && pendingFinancialSettingsId && userRegistered.id) {
                try {
                  await associateFinancialSettings({
                    financialSettingsId: pendingFinancialSettingsId,
                    sellerId: userRegistered.id,
                    creditType: CorbanCreditModalityEnum.CONSIG_WORKER,
                  });
                } catch (error: any) {
                  console.error("Erro ao associar financial settings:", error);
                  toast({
                    title: "Aviso",
                    description: "Usuário criado com sucesso, mas houve um erro ao associar as configurações financeiras.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                  });
                }
              }

              toast({
                title: `Usuário ${isNew ? "cadastrado" : "editado"} com sucesso`,
                description: `Usuário ${userRegistered.name} foi ${isNew ? "cadastrado" : "editado"}!`,
                status: "success",
                duration: 3000,
                isClosable: true,
              });
              navigate(-1);
            } catch (error: any) {
              const errorMessage = 
                error?.response?.data?.message || 
                error?.message || 
                "Erro ao registrar usuário";
              
              toast({
                title: "Erro ao registrar usuário",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          }}
          validationSchema={RegisterUserFormValidationSchema(isNew)}
          autocomplete="off"
        >
          {({ isSubmitting, errors, touched, values }) => (
            <Form>
              <VStack spacing={8} align="stretch" width="100%">
                {" "}
                <HStack
                  spacing={6}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Field name="name">
                    {({ field, form }: any) => (
                      <FormControl isInvalid={!!errors.name && touched.name}>
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                        >
                          NOME
                        </FormLabel>
                        <Input
                          {...field}
                          placeholder="Nome"
                          fontFamily="Lexend Deca"
                          height={{ base: "75px", md: "50px" }}
                          fontSize={{ base: "xl", md: "lg" }}
                          px={{ base: 6, md: 4 }}
                          onBlur={() => {
                            form.setFieldTouched("name", true);
                          }}
                        />
                        <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="lastName">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={!!errors.lastName && touched.lastName}
                      >
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                        >
                          SOBRENOME
                        </FormLabel>
                        <Input
                          {...field}
                          placeholder="Sobrenome"
                          fontFamily="Lexend Deca"
                          height={{ base: "75px", md: "50px" }}
                          fontSize={{ base: "xl", md: "lg" }}
                          px={{ base: 6, md: 4 }}
                          onBlur={() => {
                            form.setFieldTouched("lastName", true);
                          }}
                        />
                        <FormErrorMessage>
                          {form.errors.lastName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </HStack>
                <HStack
                  spacing={6}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Field name="cellphone">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={!!errors.cellphone && touched.cellphone}
                      >
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                        >
                          TELEFONE
                        </FormLabel>
                        <Input
                          {...field}
                          as={InputMask}
                          mask="(99) 99999-9999"
                          placeholder="(00) 00000-0000"
                          fontFamily="Lexend Deca"
                          height={{ base: "75px", md: "50px" }}
                          fontSize={{ base: "xl", md: "lg" }}
                          px={{ base: 6, md: 4 }}
                        />
                        <FormErrorMessage>
                          {form.errors.cellphone}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="document">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={!!errors.document && touched.document}
                      >
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                        >
                          CPF
                        </FormLabel>
                        <Input
                          {...field}
                          as={InputMask}
                          mask="999.999.999-99"
                          placeholder="000.000.000-00"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                          px={{ base: 6, md: 4 }}
                        />
                        <FormErrorMessage>
                          {form.errors.document}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </HStack>
                <Field name="email">
                  {({ field, form }: any) => (
                    <FormControl isInvalid={!!errors.email && touched.email}>
                      <FormLabel
                        color="black"
                        fontFamily="Lexend Deca"
                        fontSize={{ base: "xl", md: "lg" }}
                      >
                        E-MAIL
                      </FormLabel>
                      <Input
                        {...field}
                        type="email"
                        placeholder="email@exemplo.com"
                        fontFamily="Lexend Deca"
                        height={{ base: "75px", md: "50px" }}
                        fontSize={{ base: "xl", md: "lg" }}
                        px={{ base: 6, md: 4 }}
                        autoComplete="off"
                      />
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <HStack
                  spacing={6}
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Field name="password">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={!!errors.password && touched.password}
                      >
                        <FormLabel
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                        >
                          Senha
                        </FormLabel>
                        <InputGroup>
                          <Input
                            {...field}
                            type={showPassword ? "text" : "password"}
                            placeholder="********"
                            fontFamily="Lexend Deca"
                            height={{ base: "75px", md: "50px" }}
                            fontSize={{ base: "xl", md: "lg" }}
                            px={{ base: 6, md: 4 }}
                            autoComplete="new-password"
                          />
                          <InputRightElement h={{ base: "75px", md: "50px" }}>
                            <IconButton
                              fontFamily="Lexend Deca"
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              icon={
                                showPassword ? (
                                  <FiEye size={20} />
                                ) : (
                                  <FiEyeOff size={20} />
                                )
                              }
                              variant="ghost"
                              onClick={() => setShowPassword(!showPassword)}
                            />
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="confirmPassword">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={
                          !!errors.confirmPassword && touched.confirmPassword
                        }
                      >
                        <FormLabel
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                        >
                          Confirmar Senha
                        </FormLabel>
                        <InputGroup>
                          <Input
                            {...field}
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="********"
                            fontFamily="Lexend Deca"
                            height={{ base: "75px", md: "50px" }}
                            fontSize={{ base: "xl", md: "lg" }}
                            px={{ base: 6, md: 4 }}
                            autoComplete="new-password"
                          />
                          <InputRightElement h={{ base: "75px", md: "50px" }}>
                            <IconButton
                              fontFamily="Lexend Deca"
                              aria-label={
                                showConfirmPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              icon={
                                showConfirmPassword ? (
                                  <FiEye size={20} />
                                ) : (
                                  <FiEyeOff size={20} />
                                )
                              }
                              variant="ghost"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            />
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.confirmPassword}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </HStack>
                <Field name="roles">
                  {({field, form}: any) => {
                    const hasError = !!form.errors.roles && form.touched.roles;
                    const hasValue = Array.isArray(field.value) && field.value.length > 0;
                    const isInvalid = hasError && !hasValue;
                    return (
                      <FormControl
                        isInvalid={isInvalid}
                      >
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                          mb={4}
                        >
                          Selecione os Cargos
                        </FormLabel>
                        <Select
                          {...field}
                          fontFamily="Lexend Deca"
                          placeholder="Selecione uma opção"
                          minHeight={{ base: "75px", md: "50px" }}
                          fontSize={{ base: "xl", md: "lg" }}
                          width="100%"
                          value={field.value.length > 0 ? field.value[0].id : ""}
                          onChange={(e) => {
                            form.setFieldTouched("roles", true);
                            const selectedRoleId = e.target.value;
                            if (selectedRoleId) {
                              form.setFieldError("roles", undefined);
                            }
                            handleRoleChange(e, form.setFieldValue, values.corbanId?.toString());
                          }}
                          onBlur={() => {
                            form.setFieldTouched("roles", true);
                            if (field.value.length > 0) {
                              form.setFieldError("roles", undefined);
                            } else {
                              form.validateField("roles");
                            }
                          }}
                          lineHeight={{ base: "28px", md: "normal" }}
                          sx={{
                            "&::placeholder": {
                              fontSize: { base: "xl", md: "lg" },
                            },
                            "& option": {
                              fontSize: { base: "xl", md: "lg" },
                            },
                            "select::-ms-expand": {
                              display: "none",
                            },
                          }}
                        >
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{form.errors.roles}</FormErrorMessage>
                      </FormControl>
                    )
                  }}
                </Field>
                {selectedRole === "Vendedor" && (
                  <Field name="supervisorId">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.supervisorId &&
                          form.touched.supervisorId
                        }
                      >
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                          mb={4}
                        >
                          Selecione o Supervisor
                        </FormLabel>
                        <AsyncSelect
                          cacheOptions
                          defaultOptions={supervisors.map(
                            (supervisor: User) => ({
                              value: supervisor.id,
                              label: `${supervisor.name} ${supervisor.lastName}`,
                            })
                          )}
                          loadOptions={async (input: string) => {
                            setSearchSupervisor(input);
                            // const creditPromoterId = paramCreditPromoterId ?? actualUser?.creditPromoterId;
                            const corbanId = paramCorbanId ?? actualUser?.corbanId;
                            const filters = {
                              page: 1,
                              pageSize: 10,
                              roleId: roles.find(
                                (role) => role.name === "Supervisor"
                              )?.id,
                              corbanId: Number(corbanId),
                              enabled: true,
                              name: input,
                            };
                            const response = await getFilteredUsers(filters);
                            setSupervisors(response.data);
                            return response.data.map((supervisor: User) => ({
                              value: supervisor.id,
                              label: `${supervisor.name} ${supervisor.lastName}`,
                            }));
                          }}
                          onMenuScrollToBottom={async () => {
                            const nextPage = Math.ceil(supervisors.length / 10) + 1;
                            const corbanId = paramCorbanId ?? actualUser?.corbanId;
                            const filters = {
                              page: nextPage,
                              pageSize: 10,
                              roleId: roles.find(
                                (role) => role.name === "Supervisor"
                              )?.id,
                              corbanId: Number(corbanId),
                              enabled: true,
                              name: searchSupervisor,
                            };
                            const response = await getFilteredUsers(filters);
                            if (response.data.length) {
                              setSupervisors([
                                ...supervisors,
                                ...response.data,
                              ]);
                            }
                          }}
                          onInputChange={(value) => {
                            setSearchSupervisor(value);
                            return value;
                          }}
                          onChange={(option) => {
                            form.setFieldValue("supervisorId", option?.value);
                          }}
                          placeholder="Selecione um supervisor"
                          isDisabled={loadingSupervisors}
                          inputValue={searchSupervisor}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "50px",
                              fontFamily: "Lexend Deca",
                              fontSize: "lg",
                            }),
                          }}
                          value={
                            field.value || actualUser?.ownerId
                              ? {
                                value: field.value || actualUser?.ownerId,
                                label: supervisors.find(
                                  (supervisor: User) =>
                                    supervisor.id === field.value
                                )
                                  ? `${supervisors.find((supervisor: User) => supervisor.id === field.value)?.name} ${supervisors.find((supervisor: User) => supervisor.id === field.value)?.lastName}`
                                  : `${actualUser?.owner?.name} ${actualUser?.owner?.lastName}`,
                              }
                              : null
                          }
                        />
                        <FormErrorMessage>
                          {form.errors.supervisorId}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                )}
                {isPromoter && !paramCorbanId && !actualUser?.corbanId && (
                  <Field name="creditPromoterBusinessName">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.creditPromoterBusinessName &&
                          form.touched.creditPromoterBusinessName
                        }
                      >
                        <FormLabel
                          color="black"
                          fontFamily="Lexend Deca"
                          fontSize={{ base: "xl", md: "lg" }}
                          mb={4}
                        >
                          Promotora
                        </FormLabel>
                        <Input
                          {...field}
                          readOnly
                          value={creditPromoter?.businessName}
                          fontFamily="Lexend Deca"
                          minHeight={{ base: "75px", md: "50px" }}
                          fontSize={{ base: "xl", md: "lg" }}
                          width="100%"
                          bg="gray.100"
                        />
                        <FormErrorMessage>
                          {form.errors.creditPromoterBusinessName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                )}
                {((!isNew && actualUser?.isAllowedFinancialSettingsCorbanId) ||
                  (isNew && isAllowedFinancialSettings)) && (
                    <Button
                      type="button"
                      bg="purple.500"
                      color="white"
                      px={{ base: 8, md: 6 }}
                      py={{ base: 8, md: 4 }}
                      borderRadius="md"
                      fontWeight="bold"
                      _hover={{ bg: "purple.400" }}
                      display="flex"
                      alignItems="center"
                      w={{ base: "full", md: "md" }}
                      fontFamily="Lexend Deca"
                      alignSelf={{ base: "stretch", md: "center" }}
                      fontSize={{ base: "lg", md: "lg" }}
                      height={{ base: "70px", md: "55px" }}
                      lineHeight={{ base: "28px", md: "normal" }}
                      onClick={(e) => handleOpenFinancialSettingsModal(e, values)}
                    >
                      {financialSettingsButtonText}
                    </Button>
                  )}
                <Button
                  type="submit"
                  colorScheme="primary"
                  color="white"
                  px={{ base: 8, md: 6 }}
                  py={{ base: 8, md: 4 }}
                  borderRadius="md"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  w={{ base: "full", md: "md" }}
                  leftIcon={<FaUserPlus size={24} />}
                  fontFamily="Lexend Deca"
                  alignSelf={{ base: "stretch", md: "center" }}
                  fontSize={{ base: "lg", md: "lg" }}
                  height={{ base: "70px", md: "55px" }}
                  lineHeight={{ base: "28px", md: "normal" }}
                  isDisabled={isSubmitting}
                >
                  {!isNew ? "Editar Usuário" : "Adicionar Usuário"}
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </Box>

      {/* Modal de Financial Settings */}
      <Modal
        isOpen={
          isOpen &&
          ((!isNew && actualUser?.isAllowedFinancialSettingsCorbanId === true) ||
            (isNew && isAllowedFinancialSettings))
        }
        onClose={onClose}
        size="6xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader fontFamily="Lexend Deca">
            {financialSettingsModalTitle}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {((!isNew && actualUser && actualUser.isAllowedFinancialSettingsCorbanId) ||
              (isNew && isAllowedFinancialSettings && formValuesForFinancialSettings)) && (
                <FinancialSettingsForm
                  isSeller={true}
                  banks={banks}
                  loadingBanks={loadingBanks}
                  isNew={
                    !isNew
                      ? !actualUser?.financialSettings ||
                      actualUser.financialSettings.length === 0 ||
                      !actualUser.financialSettings[0]?.FinancialSettings
                      : true
                  }
                  initialData={
                    !isNew &&
                      actualUser?.financialSettings &&
                      actualUser.financialSettings.length > 0 &&
                      actualUser.financialSettings[0]?.FinancialSettings
                      ? {
                        ...actualUser.financialSettings[0].FinancialSettings,
                        FinancialSettingsTax:
                          (actualUser.financialSettings[0].FinancialSettings
                            ?.FinancialSettingsTax as FinancialSettingsTax[]) || [],
                      }
                      : undefined
                  }
                  corbanId={
                    !isNew
                      ? actualUser?.corbanId || undefined
                      : undefined // Não passar corbanId no modo de criação para evitar atualizar o corban
                  }
                  document={
                    !isNew
                      ? actualUser?.document
                      : formValuesForFinancialSettings?.document
                  }

                  onSubmit={async (data) => {
                    try {
                      const hasFinancialSettings =
                        !isNew &&
                        actualUser?.financialSettings &&
                        actualUser.financialSettings.length > 0 &&
                        actualUser.financialSettings[0]?.FinancialSettings;

                      if (isNew && data.financialSettingsId) {
                        setPendingFinancialSettingsId(data.financialSettingsId);

                        toast({
                          title: "Configurações financeiras salvas",
                          description:
                            "Os dados financeiros foram salvos. Serão associados ao usuário após a criação.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });

                        onClose();
                      } else if (!isNew && !hasFinancialSettings && data.financialSettingsId) {
                        if (actualUser?.id) {
                          const creditType =
                            actualUser.financialSettings?.[0]?.creditType ||
                            CorbanCreditModalityEnum.CONSIG_WORKER;

                          await associateFinancialSettings({
                            financialSettingsId: data.financialSettingsId,
                            sellerId: actualUser.id,
                            creditType: creditType,
                          });
                        }

                        // Recarregar dados do usuário
                        if (id) {
                          const updatedUser = await getUserById(id);
                          if (updatedUser) {
                            setActualUser(updatedUser);
                          }
                        }

                        toast({
                          title: "Configurações financeiras criadas com sucesso",
                          description:
                            "Os dados financeiros foram criados e associados ao usuário.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });

                        onClose();
                      } else if (!isNew && hasFinancialSettings) {
                        // No modo de edição, se já houver financial settings, apenas atualizar
                        // Recarregar dados do usuário
                        if (id) {
                          const updatedUser = await getUserById(id);
                          if (updatedUser) {
                            setActualUser(updatedUser);
                          }
                        }

                        toast({
                          title: "Configurações financeiras atualizadas com sucesso",
                          description:
                            "Os dados financeiros foram atualizados.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });

                        onClose();
                      }
                    } catch (error: any) {
                      toast({
                        title: "Erro ao salvar configurações financeiras",
                        description: error.message,
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  }}
                />
              )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
}